<template>
  <div
    class="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-[15px] pointer-events-none"
    :style="{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }"
  >
    <div
      class="pointer-events-auto relative flex h-[62px] items-center rounded-full bg-white/70 px-[4px] backdrop-blur-xl shadow-[0_3px_28px_0_rgba(0,0,0,0.10)]"
    >
      <div
        class="pointer-events-none absolute top-[4px] left-[4px] h-[54px] w-[78px] rounded-[27px] bg-[#787880]/15 backdrop-blur-xl transition-all duration-150 ease-out"
        :class="activeIndex < 0 ? 'opacity-0' : 'opacity-100'"
        :style="{ transform: `translateX(${Math.max(activeIndex, 0) * 78}px)` }"
      />
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="relative flex h-[54px] w-[78px] flex-col items-center justify-center gap-[5px]"
        @click="tab.action"
      >
        <div class="relative flex size-[22px] items-center justify-center">
          <component
            :is="activeKey === tab.key ? tab.activeIcon : tab.icon"
            class="size-[22px]"
            :class="activeKey === tab.key ? 'text-green-900' : 'text-gray-700'"
          />
          <span
            v-if="tab.badge && hasUnread"
            class="absolute -top-[3px] -right-[3px] size-[13px] rounded-full border-[1.5px] border-white bg-red-700"
          />
        </div>
        <span
          class="text-[10px] leading-[12px] tracking-[-0.1px]"
          :class="
            activeKey === tab.key
              ? 'font-medium text-green-900'
              : 'font-normal text-gray-700'
          "
        >
          {{ tab.label }}
        </span>
      </button>
    </div>

    <button
      type="button"
      class="pointer-events-auto flex size-[50px] items-center justify-center rounded-full bg-green-900 shadow-[0_3px_28px_0_rgba(0,0,0,0.10)]"
      :class="{ 'pointer-events-none opacity-50': !canStartUpload }"
      @click="handleUpload"
    >
      <FileAddFilledIcon class="size-[24px] text-white" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  HomeIcon,
  HomeFilledIcon,
  FileDocketIcon,
  FileDocketFilledIcon,
  BellIcon,
  BellFilledIcon,
  FileAddFilledIcon,
} from '@dacr/icons'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'

const props = withDefaults(
  defineProps<{
    notificationsOpen?: boolean
    hasUnread?: boolean
  }>(),
  {
    notificationsOpen: false,
    hasUnread: false,
  }
)

const emit = defineEmits<{
  activity: []
  navigate: []
}>()

const route = useRoute()
const router = useRouter()
const { showUploadDialog, canStartUpload } = useUploadFlow()
const aiEnabled = useFeatureFlag(FeatureFlag.AI)

const tabs = computed(() => [
  {
    key: 'home',
    label: 'Home',
    icon: HomeIcon,
    activeIcon: HomeFilledIcon,
    badge: false,
    action: () => {
      emit('navigate')
      router.push('/home')
    },
  },
  ...(aiEnabled.value
    ? [
        {
          key: 'docket',
          label: 'Docket',
          icon: FileDocketIcon,
          activeIcon: FileDocketFilledIcon,
          badge: false,
          action: () => {
            emit('navigate')
            router.push('/infringement')
          },
        },
      ]
    : []),
  {
    key: 'activity',
    label: 'Activity',
    icon: BellIcon,
    activeIcon: BellFilledIcon,
    badge: true,
    action: () => emit('activity'),
  },
])

const activeKey = computed(() => {
  if (props.notificationsOpen) return 'activity'
  if (aiEnabled.value && route.path.startsWith('/infringement')) return 'docket'
  if (route.path.startsWith('/home')) return 'home'
  return null
})

const activeIndex = computed(() =>
  tabs.value.findIndex((tab) => tab.key === activeKey.value)
)

const handleUpload = () => {
  showUploadDialog()
}
</script>
